import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Search, Bot, Database, Eye, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const MethodologyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/50">
                <Scale className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                Policy Document
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Methodology & Verification Policy
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Last updated: February 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Purpose */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-green-600 dark:text-green-400" />
                Purpose
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt uses a structured, transparent methodology to support responsible verification while avoiding editorial advocacy or opinion enforcement.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Verification Approach */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-green-600 dark:text-green-400" />
                Verification Approach
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">KaiCheckIt supports verification by:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Identifying claims within user-submitted content</li>
                    <li>Assessing claim verifiability</li>
                    <li>Reviewing source credibility and provenance</li>
                    <li>Distinguishing between <strong>fact</strong>, <strong>conjecture</strong>, and <strong>unverified information</strong></li>
                  </ul>
                  <p className="text-slate-700 dark:text-slate-300 font-medium border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 py-2">
                    KaiCheckIt does <strong>not</strong> determine what users must believe.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Role of AI */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-green-600 dark:text-green-400" />
                Role of AI
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">KaiCheckIt uses AI systems to:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Assist with claim extraction</li>
                    <li>Surface relevant sources</li>
                    <li>Support structured analysis and explanation</li>
                  </ul>
                  <p className="text-slate-700 dark:text-slate-300">
                    Human oversight, methodological rules, and quality controls guide system behavior.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <p className="text-amber-800 dark:text-amber-300 font-medium">
                      AI outputs are treated as <strong>decision support</strong>, not final authority.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Source Evaluation */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-green-600 dark:text-green-400" />
                Source Evaluation
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">Sources are assessed based on:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Origin and authorship</li>
                    <li>Transparency and accountability</li>
                    <li>Evidence quality</li>
                    <li>Corroboration across independent references</li>
                  </ul>
                  <p className="text-slate-600 dark:text-slate-400 text-sm italic">
                    Source credibility is contextual and may change over time.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Transparency */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
                Transparency
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">Where feasible, KaiCheckIt:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Explains how conclusions were reached</li>
                    <li>Identifies uncertainty or data gaps</li>
                    <li>Separates factual findings from interpretation</li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Limitations */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-green-600 dark:text-green-400" />
                Limitations
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">KaiCheckIt acknowledges:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>Some claims cannot be conclusively verified</li>
                    <li>Information ecosystems evolve</li>
                    <li>New evidence may require reassessment</li>
                  </ul>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Button variant="outline" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Transparency & Accountability
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          © 2025 Kaiventive Holdings. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default MethodologyPage;
